import * as React from 'react';

import { ContainerType } from '@rematter/frontend-lib-api-client';
import { Button, cn, TextField } from '@rematter/ui-web';
import { useSetAtom, useAtomValue } from 'jotai';
import { Trash2, CirclePlus } from 'lucide-react';

import * as state from '../../_state/bookingForm.state';

import { useValidateBookingContainersStep } from './hooks/useValidateBookingContainersStep';

import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { SelectContainerType } from '~/shared/components/select-fields/SelectContainerType';

export interface WizardStepContainersRef {
  isValid: (isDraft: boolean) => boolean;
}

export const BookingWizardStepContainers =
  React.forwardRef<WizardStepContainersRef>(
    (_, ref: React.Ref<WizardStepContainersRef>) => {
      const t = useTranslations();
      const addAnotherContainerButtonRef =
        React.useRef<HTMLButtonElement>(null);
      const updateBookingForm = useSetAtom(state.$updateBookingForm);
      const bookingForm = useAtomValue(state.$bookingForm);
      const containerTypes = useAtomValue(state.$containerTypes);
      const { validateContainersStep } = useValidateBookingContainersStep();
      const [validationErrors, setValidationErrors] = React.useState<
        Record<string, string>
      >({});

      React.useImperativeHandle(
        ref,
        () => ({
          isValid: (isDraft: boolean) => {
            const errors = validateContainersStep(bookingForm, isDraft, t);
            const hasErrors = Object.keys(errors).length > 0;

            if (hasErrors) {
              setValidationErrors(errors);
              return false;
            }

            return true;
          },
        }),
        [validateContainersStep, bookingForm, t],
      );

      const updateLocalContainers = (
        index: number,
        updates: Partial<{
          containerType: ContainerType;
          salesOrderId?: string | null;
          quantity: number;
        }>,
      ) => {
        updateBookingForm({
          containers: bookingForm.containers?.map((c, i) =>
            i === index ? { ...c, ...updates } : c,
          ),
        });
      };

      const handleAddContainer = () => {
        updateBookingForm({
          ...bookingForm,
          containers: [
            ...(bookingForm.containers ?? []),
            { containerType: { id: '', name: '' }, quantity: 0 },
          ],
        });
      };

      const removeContainer = (index: number) => {
        updateBookingForm({
          containers: bookingForm.containers?.filter((_, i) => i !== index),
        });

        addAnotherContainerButtonRef.current?.focus?.();
      };

      if (!bookingForm.containers || bookingForm.containers.length === 0) {
        handleAddContainer();
      }

      return (
        <div className="@BookingWizardStepContainers flex w-full flex-col gap-4">
          <div className="text-base font-semibold text-neutral/90">
            {t.bookingsView.form.sections.containers}
          </div>
          <div className="flex flex-col gap-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="text-sm font-medium text-neutral/90">
                {t.bookingsView.form.fields.containerType}
              </div>
              <div className="text-sm font-medium text-neutral/90">
                {t.bookingsView.form.fields.containersAmount}
              </div>
            </div>
            <div className="grid w-full grid-cols-2 gap-[10px]">
              {bookingForm.containers?.map((container, index) => (
                <React.Fragment key={index}>
                  <div
                    id={`container-type-${index}`}
                    className={cn(index > 0 && 'flex-1')}
                  >
                    <SelectContainerType
                      placeholder={
                        t.bookingsView.form.placeholders.selectContainerType
                      }
                      value={container.containerType.id ?? ''}
                      onChange={(_, selectedContainerType) => {
                        const containerType =
                          containerTypes.data?.containerTypes.find(
                            (containerType) =>
                              containerType.id === selectedContainerType?.id,
                          );

                        return updateLocalContainers(index, {
                          containerType: containerType as ContainerType,
                        });
                      }}
                      errorMessage={
                        validationErrors[`container_${index}_containerType`]
                      }
                      isInvalid={
                        !!validationErrors[`container_${index}_containerType`]
                      }
                    />
                  </div>
                  <div className="flex flex-1 flex-row gap-2">
                    <TextField
                      value={container.quantity?.toString() ?? ''}
                      onChange={(value) =>
                        updateLocalContainers(index, {
                          quantity: value ? parseInt(value) : 0,
                        })
                      }
                      className="@QuantityField"
                      type="number"
                      errorMessage={
                        validationErrors[`container_${index}_quantity`]
                      }
                    />

                    <Button
                      variant="outline"
                      size="small"
                      className="h-input"
                      onPress={(e) => {
                        (e.target as HTMLElement)?.blur?.();
                        removeContainer(index);
                      }}
                      isDisabled={bookingForm.containers?.length === 1}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                </React.Fragment>
              ))}
            </div>
            <div className="col-start-2 col-end-4 flex items-start justify-start">
              <Button
                ref={addAnotherContainerButtonRef}
                variant="link"
                size="small"
                className="pl-0 font-medium text-primary/75"
                onPress={handleAddContainer}
              >
                <CirclePlus className="mr-1 size-4" />
                {t.shared.addAnotherType}
              </Button>
            </div>
          </div>
        </div>
      );
    },
  );
